// Data adapter — the shared foundation every Progress view reads.
// Turns raw daily-stats + nutrition plans + active exercises into one normalized
// row per calendar day in the reporting period. Pure, no React.

import moment from "moment";

import { planForUtcDate } from "./dateRange";
import { kcalFromMacros } from "./adherence";
import {
  getNormalizedLogs,
  getEffectiveEndDate,
} from "../../views/athlete-portal/calendar/menstrual-cycle/menstrual-cycle.helper";

const DEFAULT_PERIOD_DURATION = 5;

// The five macros tracked everywhere, in display order. "kcal" is derived for
// consumed and stored-or-derived for the target (see targetForDay).
export const MACROS = ["proteins", "carbs", "fats", "fibers", "kcal"];

// Target macros for one day given the plan covering it and whether it's a
// training day. Mirrors getTargetData's per-day (non-period) rule:
// rest-day targets only when the plan defines them AND it's a rest day. kcal is
// the plan's stored `calories`, falling back to derived P×4+C×4+F×9.
const targetForDay = (plan, hasWorkout) => {
  if (!plan) return null;
  const src = plan.restDay && !hasWorkout ? plan.restDay : plan;
  return {
    proteins: src.proteins || 0,
    carbs: src.carbs || 0,
    fats: src.fats || 0,
    fibers: src.fibers || 0,
    kcal: src.calories || kcalFromMacros(src),
  };
};

// Consumed macros from a daily-stats record, kcal derived from those macros.
const consumedFromStat = (stat) => {
  const n = stat.nutrients;
  const consumed = {
    proteins: n.proteins || 0,
    carbs: n.carbs || 0,
    fats: n.fats || 0,
    fibers: n.fibers || 0,
  };
  consumed.kcal = kcalFromMacros(consumed);
  return consumed;
};

// A day is "logged" when it carries real nutrient intake. Wearable-only days
// (weight / steps / sleep but no intake) are unlogged, not absent.
const isLogged = (stat) => {
  const n = stat?.nutrients;
  return Boolean(n?.proteins || n?.carbs || n?.fats || n?.fibers);
};

// Total meal-photo count for a day. `mealPhotos` is keyed by meal type, each with
// a `list` of photos (daily-stats schema) — sum the lists. 0 when absent (the
// Calendar badge just doesn't render). Visible on unlogged days too.
const countPhotos = (mealPhotos) => {
  if (!mealPhotos || typeof mealPhotos !== "object") return 0;
  return Object.values(mealPhotos).reduce(
    (sum, meal) => sum + (Array.isArray(meal?.list) ? meal.list.length : 0),
    0
  );
};

// Training Readiness = plain sum of the 5 sub-scores (sore/stress/energy/
// mood/sleep). Same formula as the existing "Readiness Rate" report
// (TableDailyStatsComponent.js's per-day score + graph-helper.js's getAvg,
// whose chart axis is scaled 0-25) — deliberately NOT LogReadinessCard.js's
// /2-and-cap-at-5 version, which assumes a different raw scale and would
// make day/average roll-ups inconsistent with each other (plain sum is
// linear, so averaging the daily totals is always exact).
const READINESS_KEYS = ["sore", "stress", "energy", "mood", "sleep"];
const computeReadinessScore = (readiness) => {
  if (!readiness) return null;
  return READINESS_KEYS.reduce((sum, key) => sum + (readiness[key] || 0), 0);
};

// Whether a day has a food log to show (external sync or in-app meal-plan
// log) — same condition LogFoodLogCard.js uses to decide whether to render.
const hasFoodLog = (stat) =>
  Boolean(stat?.foodLog?.meals?.length || stat?.internalFoodLog?.meals?.length);

// Third-party food-log integrations that write to `foodLog.source`
// (msb-api/lib/constants.js's INTEGRATIONS, food-tracking subset — fitbit/
// ouraring/garmin sync wearables, not food) mapped to a display name.
const FOOD_SOURCE_LABELS = {
  myfitnesspal: "MyFitnessPal",
  cronometer: "Cronometer",
  macrosFirst: "MacrosFirst",
};

// How a day's food was logged, for the Day Drawer's source badge:
// - {token:"mealPlan"}          in-app meal-plan log (internalFoodLog, source "mealPlanner")
// - {token:"synced", label}     foodLog.source matches a known integration
// - {token:"manual"}            the day is logged (real nutrients) but not via either of the
//   above — covers BOTH a foodLog with real entries under an unrecognized source, AND the
//   simpler case of nutrients entered directly with no foodLog/internalFoodLog at all (a
//   quick macro-totals entry, no itemized meals)
// - null                        not logged at all this day
// A day can carry both an internalFoodLog and a synced foodLog at once (meal
// plan followed + extra food synced in) — internalFoodLog wins the badge,
// same priority FoodLog.js's own source string already gives it.
export const resolveFoodLogSource = (stat) => {
  if ((stat?.internalFoodLog?.meals?.length ?? 0) > 0) return { token: "mealPlan" };
  if (stat?.foodLog?.meals?.length > 0) {
    const label = FOOD_SOURCE_LABELS[stat.foodLog.source];
    if (label) return { token: "synced", label };
  }
  return isLogged(stat) ? { token: "manual" } : null;
};

// Builds a Set of YYYYMMDD numbers that are female-cycle period days.
// Consent-gated: returns an empty Set when no data exists so period stays
// false everywhere without any extra logic in the adapter.
export const buildPeriodDatesSet = (femaleCycleLogs, femaleCycleSettings) => {
  const periodDates = new Set();
  if (!femaleCycleLogs?.length && !femaleCycleSettings?.firstRegisteredPeriod) {
    return periodDates;
  }
  const normalized = getNormalizedLogs(femaleCycleLogs, femaleCycleSettings);
  const avgDuration = femaleCycleSettings?.averagePeriodLength ?? DEFAULT_PERIOD_DURATION;
  for (const log of normalized) {
    if (!log.startDate) continue;
    const endNum = log.endDate ?? getEffectiveEndDate(log.startDate, avgDuration);
    const cursor = moment(String(log.startDate), "YYYYMMDD");
    const end = moment(String(endNum), "YYYYMMDD");
    while (cursor.isSameOrBefore(end)) {
      periodDates.add(Number(cursor.format("YYYYMMDD")));
      cursor.add(1, "day");
    }
  }
  return periodDates;
};

// Build one normalized row per calendar day in [dateFrom, dateTo], newest-first.
// Iterates every day in the range (not just days with a record) so the logged /
// total split is correct, and left-joins the daily-stats record, the plan
// covering the day, and its training/rest target.
export const buildDayRows = ({
  dailyStats = [],
  nutritionPlans = [],
  activeExercises = [],
  dateFrom,
  dateTo,
  periodDatesSet = new Set(),
}) => {
  const statByDate = new Map(dailyStats.map((s) => [s.utcDate, s]));
  const workoutDates = new Set(activeExercises.map((e) => e.utcDate));

  const rows = [];
  const cursor = moment(dateFrom).startOf("day");
  const end = moment(dateTo).startOf("day");
  while (cursor.isSameOrBefore(end)) {
    const utcDate = Number(cursor.format("YYYYMMDD"));
    const stat = statByDate.get(utcDate) || null;
    const workout = workoutDates.has(utcDate);
    const logged = isLogged(stat);
    const plan = planForUtcDate(nutritionPlans, utcDate);

    // bodyWeight sometimes arrives as a numeric string (e.g. "60") rather than a
    // number — coerce before checking finiteness so those aren't discarded.
    const rawWeight = stat?.bodyWeight;
    const weight = rawWeight != null && Number.isFinite(Number(rawWeight))
      ? Number(rawWeight)
      : null;

    rows.push({
      utcDate,
      logged,
      workout,
      dayType: workout ? "training" : "rest",
      period: periodDatesSet.has(utcDate),
      planId: plan ? String(plan._id) : null,
      target: targetForDay(plan, workout),
      consumed: logged ? consumedFromStat(stat) : null,
      weight,
      steps: stat?.steps ?? null,
      sleep: stat?.sleep ?? null, // milliseconds (daily-stats schema)
      photos: countPhotos(stat?.mealPhotos),
      hasFoodLog: hasFoodLog(stat),
      hrv: stat?.hrv ?? null, // milliseconds
      restingHeartRate: stat?.restingHeartRate ?? null, // BPM
      readinessScore: computeReadinessScore(stat?.readiness), // 0-25
      sessionRPE: stat?.sessionRPE ?? null, // 0-10
      waterIntake: stat?.waterIntake ?? null, // liters
      bodyFatPercentage: stat?.bodyFatPercentage ?? null,
    });
    cursor.add(1, "day");
  }

  return rows.sort((a, b) => b.utcDate - a.utcDate); // newest-first
};
